import { useTestLogic } from '../../hooks/useTestLogic/useTestLogic';
import { Text } from '../../components/Text';
import { Card } from '../../components/Card';
import { Loader } from '../../components/Loader';
import { cubeParams, loadingThresholdValues } from '../../constants/constants';
import { AvatarScene } from '../../components/AvatarScene';
import { RecordingBarV2 } from '../../components/RecordingBarV2';
import { ErrorCard } from '../../components/ErrorCard';
import TimerProgressBar from '../../components/timerProgressBar/TimerProgressBar';
import { secondsToMMSS } from '../../service/utils';
import EndScreen from './EndScreen';

import classes from './TestScreen.module.css';
import HOELTPart2 from './HOELTPart2';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { changeLoadingTimes } from '../../redux/appConfigSlice';
import { useEffect, useRef, useState } from 'react';

const UploadingScreen = () => (
    <Card className={classes.loadingMessageCard}>
        <Loader id="uploading" micAccess={false} />
        <Text text="Your test is being uploaded" variant="heading" fontWeight="semibold" />
        <Text text="Please wait and do not close the screen" variant="body" size="sm" />
    </Card>
);

const TestScreenV2 = () => {

     const dispatch = useDispatch();
    const { navigationStartTime } = useSelector((state: RootState) => state.appConfigSlice);
     const avatarLoadedRef = useRef(false);
    const [ isFinal2Sec, setIsFinal2Sec] = useState(false);
    const {
        loaded,
        testEnded,
        animationMode,
        cardText,
        prepTime,
        monologueTime,
        recordedChunks,
        hideVisualizer,
        setIsAnimationLoading,
        uploadingTest,
        partTimer,
        promptTimer,
        isFailedInsperaTestClosure,
        insperaDetails,
        error,
        currentPrompt,
        enableHoeltPart2,
        currentSubTitle,
        isFirstAudioPlayed,
        connection,
    } = useTestLogic();

    useEffect(() => {
        if(testEnded) {
            setTimeout(() => {
                setIsFinal2Sec(true);
            }, 2000);
        }
    },[testEnded]);
    
      useEffect(() => {
        if (!avatarLoadedRef.current && isFirstAudioPlayed && navigationStartTime) {
            avatarLoadedRef.current = true;
            const loadingDuration = (Date.now() - navigationStartTime)/1000; // in seconds
            dispatch(changeLoadingTimes({ testLoadingTime: loadingDuration }));
            console.log(`Avatar loading duration: ${loadingDuration}s`);
            if (parseFloat(loadingDuration.toFixed(2)) >  loadingThresholdValues.avatarLoadingTime) {
               console.error('Avatar loading time exceeded 5 seconds');
            }
        }
    }, [isFirstAudioPlayed, navigationStartTime, dispatch]);

    const interruptTimer = Math.min(promptTimer, partTimer); // returns the lower timer value

    if (error) return <ErrorCard errorCode={error} />;

    if (isFailedInsperaTestClosure) return <ErrorCard failedPostMessageResponse errorCode="V111" />;

    if (isFinal2Sec && uploadingTest) return <UploadingScreen />;

    if (testEnded && isFinal2Sec) return <EndScreen insperaDetails={insperaDetails} />;

    return (
        <div className={classes.container}>
            {!loaded && <Text text="Loading..." />}

            {loaded && enableHoeltPart2 && insperaDetails.product === "HOELT" && (
                <HOELTPart2 
                    currentPrompt={currentPrompt} prepTime={prepTime} animationMode={animationMode} 
                    cardText={cardText} monologueTime={monologueTime} recordedChunks={recordedChunks} 
                    hideVisualizer={hideVisualizer} setIsAnimationLoading={setIsAnimationLoading}
                    promptTimer={promptTimer} cubeParams={cubeParams}
                    currentSubTitle={currentSubTitle}
                />
            )}

            {loaded && cardText && insperaDetails.product !== "HOELT" && (
                <div className={classes.gridContainer}>
                    <Card className={classes.instructionsCard}>
                        <Text text={cardText} className={classes.instructions} />
                    </Card>

                    {!!monologueTime && (
                        <TimerProgressBar
                            timerBarDuration={monologueTime}
                            className={classes.timerProgressBar}
                            promptTimerMessage={promptTimer}
                            paused={!connection.online}
                        />
                    )}

                    {prepTime ? (
                        <Card className={classes.timer}>
                            <Text text="Preparation time left:" variant="heading" size="md" fontWeight="bold" />
                            <Text text={secondsToMMSS(prepTime)} variant="heading" size="xl" fontWeight="bold" />
                        </Card>
                    ) : (
                        <AvatarScene
                            mode={animationMode}
                            cubeParams={cubeParams.sideCube}
                            className={classes.sideAvatar}
                            animationLoading={(loading) => setIsAnimationLoading(loading)}
                        />
                    )}

                    <RecordingBarV2
                        className={classes.sideRecordingBar}
                        hideVisualizer={hideVisualizer}
                        pcmChunks={recordedChunks}
                    />
                </div>
            )}

            {loaded && !cardText && !enableHoeltPart2 && (
                <div className={classes.mainAvatarContainer}>
                    <AvatarScene
                        mode={animationMode}
                        cubeParams={cubeParams.mainCube}
                        className={classes.mainAvatar}
                        animationLoading={(loading) => setIsAnimationLoading(loading)}
                        timer={interruptTimer}
                    />
                    <RecordingBarV2  className={classes.sideRecordingBar} pcmChunks={recordedChunks} hideVisualizer={hideVisualizer} />
                </div>
            )}
        </div>
    );
};

export default TestScreenV2;
